import { CloseRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import {
  getCityMasterData,
  getDistrictMasterData,
} from "../../../../services/master/master_service";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { MasterSchemeInterface } from "../../../schemes/family_enquiries/family_enquiry_list";
import { useServicesFilterStore } from "../../../../store/use-services-filter-store";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

interface FilterServicesInterface {
  states: { id: number; value: string }[];
  category: { id: number; name: string }[];
  statusList: { id: number; name: string }[];
  genderList: MasterSchemeInterface[];
}

const popoverPaperSx = {
  elevation: 0,
  sx: {
    mt: 0.5,
    borderRadius: 2,
    border: "1px solid #ECECEC",
    boxShadow: "0px 12px 28px -8px rgba(27, 0, 27, 0.18)",
  },
} as const;

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function FilterServices({
  states,
  statusList,
  genderList,
  category,
}: FilterServicesInterface) {
  const {
    openModal,
    stateId,
    cityId,
    districtId,
    categoryId,
    selectedStatus,
    gender: selectedGender,
    startDate,
    endDate,
    setField,
    reset,
  } = useServicesFilterStore();

  // Draft filter state — only committed to the store (and therefore only
  // triggers the services API call) when "Apply Filters" is clicked.
  const [_stateId, _setStateId] = useState<number | null>(stateId);
  const [_cityId, _setCityId] = useState<number | null>(cityId);
  const [_districtId, _setDistrictId] = useState<number | null>(districtId);
  const [_categoryId, _setCategoryId] = useState<number | null>(categoryId);
  const [_selectedStatus, _setSelectedStatus] = useState<number | null>(
    selectedStatus
  );
  const [_selectedGender, _setSelectedGender] = useState<string>(
    selectedGender
  );
  const [_startDate, _setStartDate] = useState<string>(startDate);
  const [_endDate, _setEndDate] = useState<string>(endDate);

  useEffect(() => {
    if (_endDate != null && _startDate != null && _startDate > _endDate) {
      _setEndDate("");
    }
  }, [_startDate]);

  function resetDraftFromStore() {
    _setStateId(stateId);
    _setCityId(cityId);
    _setDistrictId(districtId);
    _setCategoryId(categoryId);
    _setSelectedStatus(selectedStatus);
    _setSelectedGender(selectedGender);
    _setStartDate(startDate);
    _setEndDate(endDate);
  }

  // Both City and District cascade off the selected State (siblings, not
  // chained to each other) — preserved from the original behavior.
  const { data: city } = useQuery(["cityList", _stateId], async () => {
    if (_stateId) {
      let response = await getCityMasterData(_stateId);
      return response?.data?.data?.list;
    }
  });
  const { data: district } = useQuery(["districtList", _stateId], async () => {
    if (_stateId) {
      let response = await getDistrictMasterData(_stateId);
      return response?.data?.data?.list;
    }
  });

  const handleClose = () => {
    resetDraftFromStore();
    setField("openModal", false);
  };

  const handleReset = () => {
    _setStateId(null);
    _setCityId(null);
    _setDistrictId(null);
    _setCategoryId(null);
    _setSelectedStatus(null);
    _setSelectedGender("");
    _setStartDate("");
    _setEndDate("");
    setField("openModal", false);
    reset();
  };

  const handleApply = () => {
    setField("stateId", _stateId);
    setField("cityId", _cityId);
    setField("districtId", _districtId);
    setField("categoryId", _categoryId);
    setField("selectedStatus", _selectedStatus);
    setField("gender", _selectedGender);
    setField("startDate", _startDate);
    setField("endDate", _endDate);
    setField("openModal", false);
  };

  function _dateRange({
    title,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  }: {
    title: string;
    startDate: string;
    setStartDate: (value: string) => void;
    endDate: string;
    setEndDate: (value: string) => void;
  }) {
    return (
      <Box mt={2}>
        <Typography variant="body2" fontWeight={600} color={black} mb={1}>
          {title}
        </Typography>
        <Box display="flex" gap={1.5}>
          <DatePicker
            label="Start Date"
            format="DD-MM-YYYY"
            disableFuture
            value={startDate ? dayjs(startDate, "YYYY-MM-DD") : null}
            onChange={(value: Dayjs | null) => {
              setStartDate(value?.format("YYYY-MM-DD") || "");
            }}
            slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
            sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
          />
          <DatePicker
            label="End Date"
            format="DD-MM-YYYY"
            disableFuture
            minDate={startDate ? dayjs(startDate, "YYYY-MM-DD") : undefined}
            value={endDate ? dayjs(endDate, "YYYY-MM-DD") : null}
            onChange={(value: Dayjs | null) => {
              setEndDate(value?.format("YYYY-MM-DD") || "");
            }}
            slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
            sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
          />
        </Box>
      </Box>
    );
  }

  function _dropdownOption({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
  }: {
    title: string;
    placeholder: string;
    options: { id: number; value: string }[] | undefined; // Make options potentially undefined
    selectedId: number | null;
    onSelect: (id: number | null) => void;
  }) {
    return (
      <Box mt={2}>
        <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
          {title}
        </Typography>
        <Autocomplete
          fullWidth
          size="small"
          value={
            (Array.isArray(options)
              ? options.find((option) => selectedId === option?.id)
              : undefined) || null
          }
          onChange={(e, value) => {
            onSelect(value?.id || null);
          }}
          options={Array.isArray(options) ? options : []}
          getOptionLabel={(option) => option?.value || ""}
          componentsProps={{
            paper: popoverPaperSx,
            popper: {
              disablePortal: true,
              placement: "bottom-start",
              modifiers: [{ name: "flip", enabled: false }],
            },
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder={placeholder} />
          )}
        />
      </Box>
    );
  }

  return (
    <Dialog open={openModal} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 440, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Services
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3, maxHeight: "70vh", overflow: "auto" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          {_dropdownOption({
            title: "State",
            placeholder: "Search for State",
            options: states,
            selectedId: _stateId,
            onSelect: (id) => {
              _setStateId(id);
              _setCityId(null);
              _setDistrictId(null);
            },
          })}
          {_dropdownOption({
            title: "District",
            placeholder: "Search for District",
            options: district,
            selectedId: _districtId,
            onSelect: (id) => {
              _setDistrictId(id);
            },
          })}
          {_dropdownOption({
            title: "City",
            placeholder: "Search for City",
            options: city,
            selectedId: _cityId,
            onSelect: (id) => {
              _setCityId(id);
            },
          })}
          {_dropdownOption({
            title: "Category",
            placeholder: "Search for Category",
            options: category.map((item) => ({ id: item.id, value: item.name })),
            selectedId: _categoryId,
            onSelect: (id) => {
              _setCategoryId(id);
            },
          })}
          {_dateRange({
            title: "Date Range",
            setEndDate: _setEndDate,
            setStartDate: _setStartDate,
            startDate: _startDate,
            endDate: _endDate,
          })}

          <Typography variant="body2" fontWeight={600} color={black} mt={2} mb={1}>
            Gender
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {genderList?.map((gender) => (
              <Chip
                key={gender?.value}
                label={gender?.value}
                clickable
                variant={_selectedGender === gender.value ? "filled" : "outlined"}
                color="primary"
                onClick={() => {
                  _setSelectedGender(_selectedGender === gender?.value ? "" : gender?.value || "");
                }}
              />
            ))}
          </Box>

          <Typography variant="body2" fontWeight={600} color={black} mt={2} mb={1}>
            Status
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {statusList?.map((item) => (
              <Chip
                key={item?.id}
                label={item?.name}
                clickable
                variant={_selectedStatus === item?.id ? "filled" : "outlined"}
                color="primary"
                onClick={() => {
                  _setSelectedStatus(_selectedStatus === item?.id ? null : item?.id);
                }}
              />
            ))}
          </Box>

          <Box display="flex" gap={1.5} mt={4}>
            <Button
              fullWidth
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleApply}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
